"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateNewPluginManifestSentinel = exports.TeamsProjectTypeOptions = void 0;
exports.getTeamsProjectNode = getTeamsProjectNode;
exports.customCopilotRagNode = customCopilotRagNode;
exports.notificationBotTriggerNode = notificationBotTriggerNode;
exports.botProjectTypeNode = botProjectTypeNode;
exports.tabProjectTypeNode = tabProjectTypeNode;
exports.meProjectTypeNode = meProjectTypeNode;
exports.m365SearchMeSubNode = m365SearchMeSubNode;
exports.MCPServerTypeNode = MCPServerTypeNode;
exports.MCPToolsFileNode = MCPToolsFileNode;
exports.MCPCliPreFetchToolsNode = MCPCliPreFetchToolsNode;
exports.MCPLocalServerSelectionNode = MCPLocalServerSelectionNode;
exports.MCPForDAServerUrlNode = MCPForDAServerUrlNode;
exports.updateActionWithMCP = updateActionWithMCP;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs = tslib_1.__importStar(require("fs-extra"));
const path_1 = tslib_1.__importDefault(require("path"));
const localizeUtils_1 = require("../../../common/localizeUtils");
const mcpToolFetcher_1 = require("../../../component/utils/mcpToolFetcher");
const odrProvider_1 = require("../../../component/utils/odrProvider");
const create_1 = require("../../create");
const questionNames_1 = require("../../questionNames");
const commonNodes_1 = require("../commonNodes");
const CapabilityOptions_1 = require("./CapabilityOptions");
const rootNode_1 = require("./rootNode");
/**
 * Extract the Teams Agents and Apps sub-tree from the combined wizardNode.json.
 * Used by TDP (Teams Developer Portal) flow.
 */
function getTeamsProjectNode() {
    const root = (0, rootNode_1.getRootProjectTypeNode)(teamsfx_api_1.Platform.VSCode);
    const teamsNode = root.children?.find((c) => c.condition?.equals === "teams-agent-and-app-type");
    return teamsNode ?? { data: { type: "group" } };
}
class TeamsProjectTypeOptions {
    static tabOptionId = "tab-type";
    static botOptionId = "bot-type";
    static meOptionId = "me-type";
    static tab(platform = teamsfx_api_1.Platform.VSCode) {
        return {
            id: TeamsProjectTypeOptions.tabOptionId,
            label: `${platform === teamsfx_api_1.Platform.VSCode ? "$(browser) " : ""}${(0, localizeUtils_1.getLocalizedString)("core.TabOption.label")}`,
            detail: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.tab.detail"),
        };
    }
    static bot(platform = teamsfx_api_1.Platform.VSCode) {
        return {
            id: TeamsProjectTypeOptions.botOptionId,
            label: `${platform === teamsfx_api_1.Platform.VSCode ? "$(hubot) " : ""}${(0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.bot.label")}`,
            detail: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.bot.detail"),
        };
    }
    static me(platform = teamsfx_api_1.Platform.VSCode) {
        return {
            id: TeamsProjectTypeOptions.meOptionId,
            label: `${platform === teamsfx_api_1.Platform.VSCode ? "$(symbol-keyword) " : ""}${(0, localizeUtils_1.getLocalizedString)("core.MessageExtensionOption.label")}`,
            detail: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.messageExtension.copilotEnabled.detail"),
        };
    }
}
exports.TeamsProjectTypeOptions = TeamsProjectTypeOptions;
function customCopilotRagNode() {
    return {
        condition: { equals: CapabilityOptions_1.TeamsAgentCapabilityOptions.customCopilotRag().id },
        data: {
            type: "singleSelect",
            name: questionNames_1.QuestionNames.CustomCopilotRag,
            title: (0, localizeUtils_1.getLocalizedString)("template.teams.rag.label"),
            placeholder: (0, localizeUtils_1.getLocalizedString)("template.teams.rag.source.placeholder"),
            staticOptions: [
                CapabilityOptions_1.CustomCopilotRagOptions.customize(),
                CapabilityOptions_1.CustomCopilotRagOptions.azureAISearch(),
                CapabilityOptions_1.CustomCopilotRagOptions.customApi(),
                // CustomCopilotRagOptions.microsoft365(),
            ],
            default: CapabilityOptions_1.CustomCopilotRagOptions.customize().id,
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
        children: [(0, commonNodes_1.apiSpecNode)({ equals: CapabilityOptions_1.CustomCopilotRagOptions.customApi().id })],
    };
}
// export function aiAgentNode(): IQTreeNode {
//   return {
//     condition: { equals: CustomCopilotCapabilityOptions.aiAgent().id },
//     data: {
//       type: "singleSelect",
//       name: QuestionNames.CustomCopilotAssistant,
//       title: getLocalizedString(
//         "core.createProjectQuestion.capability.customCopilotAssistant.title"
//       ),
//       placeholder: getLocalizedString(
//         "core.createProjectQuestion.capability.customCopilotAssistant.placeholder"
//       ),
//       staticOptions: [
//         CustomCopilotAssistantOptions.new(),
//         CustomCopilotAssistantOptions.assistantsApi(),
//       ],
//       default: CustomCopilotAssistantOptions.new().id,
//       onDidSelection: setTemplateName,
//     },
//   };
// }
function notificationBotTriggerNode(platform = teamsfx_api_1.Platform.VSCode) {
    return {
        condition: { equals: CapabilityOptions_1.BotCapabilityOptions.notificationBotId },
        data: {
            name: questionNames_1.QuestionNames.BotTrigger,
            title: (0, localizeUtils_1.getLocalizedString)("plugins.bot.questionHostTypeTrigger.title"),
            type: "singleSelect",
            cliDescription: "Specifies the trigger for `Chat Notification Message` app template.",
            staticOptions: [
                platform === teamsfx_api_1.Platform.VS
                    ? CapabilityOptions_1.NotificationBotOptions.appServiceForVS()
                    : CapabilityOptions_1.NotificationBotOptions.appService(),
                CapabilityOptions_1.NotificationBotOptions.functionsHttpAndTimerTrigger(),
                CapabilityOptions_1.NotificationBotOptions.functionsHttpTrigger(),
                CapabilityOptions_1.NotificationBotOptions.functionsTimerTrigger(),
            ],
            default: platform === teamsfx_api_1.Platform.VS
                ? CapabilityOptions_1.NotificationBotOptions.appServiceForVS().id
                : CapabilityOptions_1.NotificationBotOptions.appService().id,
            placeholder: (0, localizeUtils_1.getLocalizedString)("plugins.bot.questionHostTypeTrigger.placeholder"),
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
    };
}
function botProjectTypeNode() {
    return {
        // project-type = Bot
        condition: { equals: TeamsProjectTypeOptions.botOptionId },
        data: {
            name: questionNames_1.QuestionNames.Capabilities,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.bot.title"),
            type: "singleSelect",
            staticOptions: [
                CapabilityOptions_1.BotCapabilityOptions.basicBot(),
                CapabilityOptions_1.BotCapabilityOptions.notificationBot(),
                CapabilityOptions_1.BotCapabilityOptions.commandBot(),
                CapabilityOptions_1.BotCapabilityOptions.workflowBot(),
            ],
            placeholder: (0, localizeUtils_1.getLocalizedString)("template.createCapabilityQuestion.placeholder"),
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
        children: [notificationBotTriggerNode()],
    };
}
function tabProjectTypeNode(platform = teamsfx_api_1.Platform.VSCode) {
    return {
        // project-type = Tab
        condition: { equals: TeamsProjectTypeOptions.tabOptionId },
        data: {
            name: questionNames_1.QuestionNames.Capabilities,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.tab.title"),
            type: "singleSelect",
            staticOptions: [
                CapabilityOptions_1.TabCapabilityOptions.nonSsoTab(),
                CapabilityOptions_1.TabCapabilityOptions.m365SsoLaunchPage(),
                CapabilityOptions_1.TabCapabilityOptions.dashboardTab(),
                CapabilityOptions_1.TabCapabilityOptions.SPFxTab(),
            ],
            placeholder: (0, localizeUtils_1.getLocalizedString)("template.createCapabilityQuestion.placeholder"),
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
        children: [
            {
                //SPFx sub-tree
                condition: { equals: CapabilityOptions_1.TabCapabilityOptions.SPFxTab().id },
                data: (0, create_1.SPFxSolutionQuestion)(),
                children: [
                    {
                        data: { type: "group" },
                        children: [
                            { data: (0, create_1.SPFxPackageSelectQuestion)() },
                            { data: (0, create_1.SPFxFrameworkQuestion)() },
                            { data: (0, create_1.SPFxWebpartNameQuestion)() },
                        ],
                        condition: { equals: "new" },
                    },
                    {
                        data: (0, create_1.SPFxImportFolderQuestion)(),
                        condition: { equals: "import" },
                    },
                ],
            },
        ],
    };
}
function meProjectTypeNode() {
    return {
        // project-type = Messaging Extension
        condition: { equals: TeamsProjectTypeOptions.meOptionId },
        data: {
            name: questionNames_1.QuestionNames.Capabilities,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.projectType.messageExtension.title"),
            type: "singleSelect",
            staticOptions: [
                CapabilityOptions_1.MeCapabilityOptions.m365SearchMe(),
                CapabilityOptions_1.MeCapabilityOptions.collectFormMe(),
                CapabilityOptions_1.MeCapabilityOptions.linkUnfurling(),
            ],
            placeholder: (0, localizeUtils_1.getLocalizedString)("template.createCapabilityQuestion.placeholder"),
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
        children: [m365SearchMeSubNode()],
    };
}
function m365SearchMeSubNode() {
    return {
        // Search ME sub-tree
        condition: { equals: CapabilityOptions_1.MeCapabilityOptions.m365SearchMe().id },
        data: {
            name: questionNames_1.QuestionNames.MeArchitectureType,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.meArchitecture.title"),
            cliDescription: "The authentication type for the API.",
            type: "singleSelect",
            staticOptions: [
                CapabilityOptions_1.MeArchitectureOptions.newApi(),
                CapabilityOptions_1.MeArchitectureOptions.openApiSpec(),
                CapabilityOptions_1.MeArchitectureOptions.botMe(),
            ],
            default: CapabilityOptions_1.MeArchitectureOptions.newApi().id,
            placeholder: (0, localizeUtils_1.getLocalizedString)("template.createProjectQuestion.projectType.copilotExtension.placeholder"),
            forgetLastValue: true,
            skipSingleOption: true,
            onDidSelection: CapabilityOptions_1.setTemplateName,
        },
        children: [
            {
                condition: { equals: CapabilityOptions_1.MeArchitectureOptions.newApi().id },
                data: {
                    type: "singleSelect",
                    name: questionNames_1.QuestionNames.ApiAuth,
                    title: (0, localizeUtils_1.getLocalizedString)("template.createProjectQuestion.apiMessageExtensionAuth.title"),
                    placeholder: (0, localizeUtils_1.getLocalizedString)("template.createProjectQuestion.apiMessageExtensionAuth.placeholder"),
                    staticOptions: [
                        CapabilityOptions_1.ApiAuthOptions.none(true),
                        CapabilityOptions_1.ApiAuthOptions.bearerToken(),
                        CapabilityOptions_1.ApiAuthOptions.microsoftEntra(true),
                    ],
                    default: CapabilityOptions_1.ApiAuthOptions.none(true).id,
                    onDidSelection: CapabilityOptions_1.setTemplateName,
                },
            },
            (0, commonNodes_1.apiSpecNode)({ equals: CapabilityOptions_1.MeArchitectureOptions.openApiSpec().id }),
        ],
    };
}
function MCPServerTypeNode() {
    return {
        condition: { equals: CapabilityOptions_1.ActionStartOptions.mcp().id },
        data: {
            name: questionNames_1.QuestionNames.MCPServerType,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.title"),
            type: "singleSelect",
            staticOptions: [],
            dynamicOptions: async (inputs) => {
                const servers = await odrProvider_1.ODRProvider.listServers();
                inputs["_McpOdrOutput"] = servers;
                const options = [
                    {
                        id: "remote",
                        label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.remote.label"),
                        detail: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.remote.detail"),
                    },
                ];
                if (servers.length > 0) {
                    options.push({
                        id: "local",
                        label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.local.label"),
                        detail: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.local.detail"),
                    });
                }
                return options;
            },
            default: "remote",
            placeholder: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpServerType.placeholder"),
            skipSingleOption: true,
        },
        children: [
            {
                condition: { equals: "remote" },
                data: MCPForDAServerUrlNode().data,
                children: [
                    MCPToolsFileNode(),
                    MCPCliPreFetchToolsNode(),
                    {
                        condition: (inputs) => {
                            if (inputs.platform === teamsfx_api_1.Platform.VSCode)
                                return false;
                            return inputs[questionNames_1.QuestionNames.MCPForDAAuth] !== "NoneAuth";
                        },
                        data: {
                            type: "singleSelect",
                            name: questionNames_1.QuestionNames.MCPForDAAuthType,
                            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.AuthType.title"),
                            staticOptions: [
                                {
                                    id: "oauth",
                                    label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.Auth.OAuth"),
                                },
                                {
                                    id: "entraSSO",
                                    label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.Auth.EntraSSO"),
                                },
                            ],
                            default: "oauth",
                        },
                    },
                ],
            },
            MCPLocalServerSelectionNode(),
        ],
    };
}
/**
 * Question node for providing an MCP tools definition file.
 * Shown when:
 * - The MCP server requires auth (auto-detected), or
 * - The user wants to provide tools manually.
 * In CLI non-interactive mode, this is provided via --mcp-tools-file.
 */
function MCPToolsFileNode() {
    return {
        data: {
            name: questionNames_1.QuestionNames.MCPToolsFilePath,
            title: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFilePath.title"),
            type: "text",
            placeholder: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFilePath.placeholder"),
            additionalValidationOnAccept: {
                validFunc: async (value, inputs) => {
                    if (!value)
                        return undefined;
                    const filePath = value;
                    if (!(await fs.pathExists(filePath))) {
                        return (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileNotFound", filePath);
                    }
                    try {
                        const tools = await (0, mcpToolFetcher_1.readMCPToolsFromFile)(filePath);
                        if (inputs) {
                            inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = tools;
                        }
                    }
                    catch (e) {
                        return e.message;
                    }
                    return undefined;
                },
            },
        },
        // This node is only shown when:
        // 1. CLI platform (no VS Code MCP gateway), AND
        // 2. Either auth is required, or auto-fetch returned no tools
        condition: (inputs) => {
            // In VS Code, tools are fetched by the extension handler, so skip this node
            if (inputs.platform === teamsfx_api_1.Platform.VSCode)
                return false;
            // Show if no available tools were populated by auto-fetch
            const tools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
            return !tools || (Array.isArray(tools) && tools.length === 0);
        },
    };
}
/**
 * Question node for CLI pre-fetch tool selection.
 * Attempts to auto-fetch tools from the MCP server URL, then presents them for selection.
 * For CLI platform only — VS Code has its own tool fetching via vscode.lm.tools.
 */
function MCPCliPreFetchToolsNode() {
    return {
        condition: (inputs) => {
            // Only show in CLI when tools are available (either fetched or loaded from file)
            if (inputs.platform === teamsfx_api_1.Platform.VSCode)
                return false;
            const tools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
            return tools && Array.isArray(tools) && tools.length > 0;
        },
        data: {
            type: "multiSelect",
            name: questionNames_1.QuestionNames.MCPForDAPreFetchTools,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.PreFetchTools.title"),
            staticOptions: [],
            dynamicOptions: (inputs) => {
                const availableTools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
                return availableTools.map((tool) => ({
                    id: tool.name,
                    label: tool.name,
                    detail: tool.description || "",
                }));
            },
            default: (inputs) => {
                const availableTools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] || [];
                return availableTools.map((tool) => tool.name);
            },
        },
    };
}
function MCPLocalServerSelectionNode() {
    return {
        condition: { equals: "local" },
        data: {
            name: questionNames_1.QuestionNames.MCPLocalServer,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpLocalServer.title"),
            type: "multiSelect",
            returnObject: true,
            staticOptions: [],
            placeholder: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpLocalServer.placeholder"),
            dynamicOptions: (inputs) => {
                const servers = inputs["_McpOdrOutput"];
                return servers.map((server) => ({
                    id: server.name,
                    label: server.display_name,
                    detail: `${server.description} (${server.tools.length} tools available)`,
                    data: JSON.stringify({
                        identifier: server.identifier,
                        command: server.command,
                        args: server.args,
                    }),
                }));
            },
            validation: { minItems: 1 },
            validationHelp: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpLocalServer.validationHelp"),
        },
        children: [],
    };
}
function MCPForDAServerUrlNode() {
    return {
        condition: { equals: CapabilityOptions_1.ActionStartOptions.mcp().id },
        data: {
            name: questionNames_1.QuestionNames.MCPForDAServerUrl,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.ServerUrl.title"),
            type: "text",
            placeholder: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.ServerUrl.placeholder"),
            additionalValidationOnAccept: {
                validFunc: async (value, inputs) => {
                    if (!value || !inputs)
                        return undefined;
                    // For CLI: attempt to auto-fetch tools from the server
                    if (inputs.platform !== teamsfx_api_1.Platform.VSCode) {
                        try {
                            const result = await (0, mcpToolFetcher_1.fetchMCPTools)(value);
                            if (result.requiresAuth) {
                                inputs["_mcpAuthRequired"] = true;
                                inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = [];
                                if (result.authMetadataUrl) {
                                    inputs[questionNames_1.QuestionNames.MCPForDAAuthMetadataUrl] = result.authMetadataUrl;
                                }
                                inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "OAuthPluginVault";
                            }
                            else if (result.tools.length > 0) {
                                inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = result.tools;
                                inputs[questionNames_1.QuestionNames.MCPForDATool] = "pre-fetch";
                                inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "NoneAuth";
                            }
                            else {
                                inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = [];
                                inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "NoneAuth";
                            }
                        }
                        catch {
                            inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = [];
                            inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "NoneAuth";
                        }
                    }
                    return undefined;
                },
            },
        },
    };
}
exports.CreateNewPluginManifestSentinel = "__createNewPluginManifest__";
function updateActionWithMCP() {
    return {
        data: {
            type: "singleFile",
            name: questionNames_1.QuestionNames.PluginManifestFilePath,
            title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.title"),
            defaultFolder: (inputs) => path_1.default.normalize(path_1.default.join(inputs.projectPath, teamsfx_api_1.AppPackageFolderName)),
            filters: { files: ["json"] },
            possibleFiles: async (inputs) => {
                const projectPath = inputs.projectPath;
                const items = [];
                // List every action plugin file referenced by the declarative agent
                // manifest so the user can update an existing one.
                try {
                    const teamsManifestPath = path_1.default.join(projectPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.ManifestTemplateFileName);
                    if (await fs.pathExists(teamsManifestPath)) {
                        const teamsManifest = await fs.readJSON(teamsManifestPath);
                        const declarativeAgentRelativePath = teamsManifest?.copilotAgents?.declarativeAgents?.[0]?.file;
                        if (declarativeAgentRelativePath) {
                            const declarativeAgentPath = path_1.default.join(projectPath, teamsfx_api_1.AppPackageFolderName, declarativeAgentRelativePath);
                            if (await fs.pathExists(declarativeAgentPath)) {
                                const da = await fs.readJSON(declarativeAgentPath);
                                const actions = da?.actions ?? [];
                                const seen = new Set();
                                for (const action of actions) {
                                    if (!action?.file)
                                        continue;
                                    const absPath = path_1.default.normalize(path_1.default.join(path_1.default.dirname(declarativeAgentPath), action.file));
                                    if (seen.has(absPath))
                                        continue;
                                    seen.add(absPath);
                                    items.push({
                                        id: absPath,
                                        label: `$(file) ${path_1.default.basename(absPath)}`,
                                        description: path_1.default.dirname(absPath),
                                    });
                                }
                            }
                        }
                    }
                }
                catch {
                    // best-effort — fall through to just the create-new option
                }
                items.push({
                    id: exports.CreateNewPluginManifestSentinel,
                    label: `$(new-file) ${(0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.label")}`,
                });
                return items;
            },
        },
        children: [
            {
                condition: (inputs) => inputs[questionNames_1.QuestionNames.PluginManifestFilePath] === exports.CreateNewPluginManifestSentinel,
                data: {
                    type: "text",
                    name: questionNames_1.QuestionNames.NewPluginManifestFileName,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.title"),
                    placeholder: teamsfx_api_1.DefaultPluginManifestFileName,
                    default: teamsfx_api_1.DefaultPluginManifestFileName,
                    validation: {
                        validFunc: async (input, inputs) => {
                            const trimmed = input.trim();
                            if (!trimmed) {
                                return (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.validation.empty");
                            }
                            if (!trimmed.toLowerCase().endsWith(".json")) {
                                return (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.validation.extension");
                            }
                            if (path_1.default.isAbsolute(trimmed) || trimmed.includes("/") || trimmed.includes("\\")) {
                                return (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.validation.relative");
                            }
                            const projectPath = inputs?.projectPath;
                            if (projectPath) {
                                const target = path_1.default.join(projectPath, teamsfx_api_1.AppPackageFolderName, trimmed);
                                if (await fs.pathExists(target)) {
                                    return (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.File.createNew.validation.exists", trimmed);
                                }
                            }
                            return undefined;
                        },
                    },
                },
            },
            {
                data: {
                    type: "multiSelect",
                    name: questionNames_1.QuestionNames.MCPForDAPreFetchTools,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.PreFetchTools.title"),
                    staticOptions: [],
                    dynamicOptions: (inputs) => {
                        const availableTools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
                        const tools = availableTools.map((tool) => {
                            return {
                                id: tool.name,
                                label: tool.name,
                                detail: tool.description || "",
                            };
                        });
                        return tools;
                    },
                    default: async (inputs) => {
                        const pluginManifestFilePath = inputs[questionNames_1.QuestionNames.PluginManifestFilePath];
                        // Skip when the user chose "Create a new ai-plugin.json" (the value
                        // is a sentinel id, not a real path) or when the file doesn't exist
                        // yet. The plugin manifest will be created later in addPluginFromMCP /
                        // updateActionWithMCP, so there are no pre-selected tools to default.
                        if (!pluginManifestFilePath ||
                            pluginManifestFilePath === exports.CreateNewPluginManifestSentinel ||
                            !(await fs.pathExists(pluginManifestFilePath))) {
                            return [];
                        }
                        const pluginManifest = await fs.readJSON(pluginManifestFilePath);
                        const serverUrl = inputs[questionNames_1.QuestionNames.MCPForDAServerUrl];
                        const result = [];
                        pluginManifest.runtimes
                            .filter((runtime) => runtime.type === "RemoteMCPServer" && runtime.spec.url === serverUrl)
                            .forEach((runtime) => {
                            result.push(...runtime["run_for_functions"]);
                        });
                        return result;
                    },
                },
            },
            {
                condition: (inputs) => {
                    return inputs[questionNames_1.QuestionNames.MCPForDAAuth] !== "NoneAuth";
                },
                data: {
                    type: "singleSelect",
                    name: questionNames_1.QuestionNames.MCPForDAAuthType,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.AuthType.title"),
                    staticOptions: [
                        {
                            id: "oauth",
                            label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.Auth.OAuth"),
                        },
                        {
                            id: "entraSSO",
                            label: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.mcpForDa.Auth.EntraSSO"),
                        },
                    ],
                    default: "oauth",
                },
            },
        ],
    };
}
//# sourceMappingURL=teamsProjectTypeNode.js.map